<template>
  <div class="flex w-72 flex-col gap-4 px-4 py-3">
    <UiComboboxMultiVee
      name="analysisFilterIDs"
      :label="$t('W: Analyser')"
      :modelValue="analysisIDs"
      :options="analysisOptions"
      :placeholder="$t('W: Alle analyser')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onAnalysisIDsChange" />

    <UiComboboxMultiVee
      v-if="isPriorityColoringEnabled"
      name="tierFilterIDs"
      :label="$t('W: Tid tilbage')"
      :modelValue="tierIDs"
      :options="tierOptions"
      :placeholder="$t('W: Alle')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onTierIDsChange" />

    <UiComboboxMultiVee
      name="locationFilterIDs"
      :label="$t('W: Lokation')"
      :modelValue="locationIDs"
      :options="locationOptions"
      :placeholder="$t('W: Alle lokationer')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onLocationIDsChange" />

    <UiComboboxMultiVee
      name="insuranceCustomerAccountFilterIDs"
      :label="$t('W: Forsikringskunde')"
      :modelValue="insuranceCustomerAccountIDs"
      :options="insuranceCustomerAccountOptions"
      :placeholder="$t('W: Alle')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onInsuranceCustomerAccountIDsChange" />

    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between gap-8">
        <span class="text-sm">{{ $t('W: Skjul afventer prøve') }}</span>
        <button
          type="button"
          class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200"
          :class="hideUnregistered ? '' : 'bg-border-subtle'"
          :style="hideUnregistered ? 'background-color: var(--color-action)' : ''"
          @click="onToggleHideUnregistered">
          <span
            class="pointer-events-none mt-0.5 inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200"
            :class="hideUnregistered ? 'translate-x-4' : 'translate-x-0.5'" />
        </button>
      </div>
      <div class="flex items-center justify-between gap-8">
        <span class="text-sm">{{ $t('W: Skjul færdige') }}</span>
        <button
          type="button"
          class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200"
          :class="hideCompleted ? '' : 'bg-border-subtle'"
          :style="hideCompleted ? 'background-color: var(--color-action)' : ''"
          @click="onToggleHideCompleted">
          <span
            class="pointer-events-none mt-0.5 inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200"
            :class="hideCompleted ? 'translate-x-4' : 'translate-x-0.5'" />
        </button>
      </div>
    </div>

    <div class="flex items-center justify-between border-t border-border-subtle pt-3">
      <button
        type="button"
        class="cursor-pointer text-sm text-action disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="!hasActiveFilters"
        @click="onClearClick">
        {{ $t('W: Ryd filtre') }}
      </button>
      <transition
        enterActiveClass="transition-opacity duration-150"
        enterFromClass="opacity-0"
        leaveActiveClass="transition-opacity duration-500"
        leaveToClass="opacity-0">
        <span v-if="showSavedFlash" class="text-xs text-text-muted">
          {{ $t('W: Gemt') }}
        </span>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { useAnalysesFilterStore } from '@/stores/analysesFilterStore'
import { mapActions, mapState } from 'pinia'

export default {
  name: 'OrdersAnalysesFilterMenu',

  emits: ['close'],

  props: {
    analysisOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    tierOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    locationOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    insuranceCustomerAccountOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    isPriorityColoringEnabled: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      showSavedFlash: false,
      savedFlashTimeout: null as ReturnType<typeof setTimeout> | null,
    }
  },

  computed: {
    ...mapState(useAnalysesFilterStore, [
      'analysisIDs',
      'tierIDs',
      'locationIDs',
      'insuranceCustomerAccountIDs',
      'hideUnregistered',
      'hideCompleted',
      'hasActiveFilters',
    ]),
  },

  methods: {
    ...mapActions(useAnalysesFilterStore, [
      'setAnalysisIDs',
      'setTierIDs',
      'setLocationIDs',
      'setInsuranceCustomerAccountIDs',
      'setHideUnregistered',
      'setHideCompleted',
      'clearFilters',
    ]),

    onAnalysisIDsChange(ids: string[]) {
      this.setAnalysisIDs(ids)
      this.flashSaved()
    },

    onTierIDsChange(ids: string[]) {
      this.setTierIDs(ids)
      this.flashSaved()
    },

    onLocationIDsChange(ids: string[]) {
      this.setLocationIDs(ids)
      this.flashSaved()
    },

    onInsuranceCustomerAccountIDsChange(ids: string[]) {
      this.setInsuranceCustomerAccountIDs(ids)
      this.flashSaved()
    },

    onToggleHideUnregistered() {
      this.setHideUnregistered(!this.hideUnregistered)
      this.flashSaved()
    },

    onToggleHideCompleted() {
      this.setHideCompleted(!this.hideCompleted)
      this.flashSaved()
    },

    onClearClick() {
      this.clearFilters()
      this.flashSaved()
    },

    flashSaved() {
      this.showSavedFlash = false
      this.$nextTick(() => {
        this.showSavedFlash = true
        if (this.savedFlashTimeout) clearTimeout(this.savedFlashTimeout)
        this.savedFlashTimeout = setTimeout(() => {
          this.showSavedFlash = false
        }, 1500)
      })
    },
  },

  beforeUnmount() {
    if (this.savedFlashTimeout) clearTimeout(this.savedFlashTimeout)
  },
}
</script>
